import React from "react";
import Icon from "./icons";
import css from "./Scheduler.module.css";
import { DOW, addDays, startOfWeek, startOfDay, sameDay, entryMeta, CAT_COLOR, fmtTimeShort, shiftDiscrepancy, DISCREPANCY_LABEL } from "./scheduleUtils";
import { offForDay, OffSummary } from "./WeekCalendar";

const MAX_CHIPS = 3;

// Label shown on a month chip (matches the prototype's monthChip):
// open -> "Open · role", appointment -> title, facility -> facility name, client -> client name.
const chipLabel = (s) => {
  if (s.status === "open") return `Open · ${s.open?.role || "shift"}`;
  if (s.kind === "appointment") return s.apptTitle || "Appointment";
  if (s.type === "facility") return s.facility?.name || "Facility";
  return entryMeta(s).clientName || s.client?.name || "Client visit";
};

export default function MonthCalendar({ shifts = [], timeOff = [], monthDate, onShiftClick, onHover, onLeave }) {
  const now = new Date();
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const gridStart = startOfWeek(first);
  // Build up to 6 weeks, then drop any week with no day in the current month, so
  // a trailing (or leading) week that belongs entirely to an adjacent month is
  // never shown.
  const cells = Array.from({ length: 6 }, (_, w) => Array.from({ length: 7 }, (_, i) => addDays(gridStart, w * 7 + i)))
    .filter((wk) => wk.some((d) => d.getMonth() === monthDate.getMonth()))
    .flat();
  const items = shifts.map((s) => ({ ...s, _date: startOfDay(new Date(s.date)) }));

  return (
    <div>
      <div className={css.monthDowRow}>
        {DOW.map((d) => (
          <div key={d} className={css.monthDow}>{d}</div>
        ))}
      </div>
      <div className={css.monthGrid}>
        {cells.map((d) => {
          const out = d.getMonth() !== monthDate.getMonth();
          const isToday = sameDay(d, now);
          const dayShifts = items.filter((s) => sameDay(s._date, d)).sort((a, b) => a.startMin - b.startMin);
          return (
            <div key={d.toISOString()} className={`${css.mcell} ${out ? css.out : ""} ${isToday ? css.today : ""}`}>
              <div className={css.mcHead}>
                <span className={css.dn}>{d.getDate()}</span>
                {dayShifts.length ? <span className={css.mcCount}>{dayShifts.length}</span> : null}
              </div>
              <div className={css.mcList}>
                <OffSummary items={offForDay(timeOff, d)} />
                {dayShifts.slice(0, MAX_CHIPS).map((s) => {
                  const meta = entryMeta(s);
                  // Colour by kind: appointments green, shifts purple, open amber.
                  const colorCat = meta.cat === "open" ? "open" : meta.appt ? "facility" : "client";
                  const color = CAT_COLOR[colorCat] || CAT_COLOR.client;
                  const appt = s.kind === "appointment";
                  const exc = shiftDiscrepancy(s);
                  return (
                    <button
                      key={s._id || s.id}
                      className={`${css.mchip} ${appt ? css.appt : ""}`}
                      style={appt ? { background: "var(--app-surface)", boxShadow: `inset 0 0 0 1px ${color.line}` } : { background: color.soft }}
                      onClick={() => onShiftClick?.(s)}
                      onMouseEnter={(e) => onHover?.(s, e)}
                      onMouseMove={(e) => onHover?.(s, e)}
                      onMouseLeave={() => onLeave?.()}
                      title={exc ? DISCREPANCY_LABEL[exc] : undefined}
                      type="button"
                    >
                      {exc ? (
                        <Icon name="alert" size={10} style={{ color: exc === "late" ? "#b45309" : "#b42318", flex: "none" }} />
                      ) : (
                        <span
                          className={css.mcDot}
                          style={appt ? { background: "transparent", border: `1.5px solid ${color.line}` } : { background: color.line }}
                        />
                      )}
                      <span className={css.mcTime} style={{ color: color.ink }}>{fmtTimeShort(s.startMin)}</span>
                      {s.recurrence?.freq && s.recurrence.freq !== "none" ? (
                        <Icon name="repeat" size={10} className={css.recurIc} style={{ marginLeft: 0 }} />
                      ) : null}
                      <span className={css.mcT}>{chipLabel(s)}</span>
                    </button>
                  );
                })}
                {dayShifts.length > MAX_CHIPS && (
                  <div className={css.mmore}>+{dayShifts.length - MAX_CHIPS} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
